import React from "react";
import { formatUSD, formatPercentage, formatLeverage, formatFloat } from "../../utils/format";
import { useLendingPoolUrl } from "../../hooks/useUrlGenerator";
import { Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import PairLeftTitle from "../PairLeftTitle";
import LiquidationPrices from "../RiskMetrics/LiquidationPrices";
import { useOffchainAccountBorrowable, useOffchainAccountLendingPool } from "../../hooks/useOffchain";
import useGenericCall from "../../hooks/useGenericCall";
import { useOnchainLendingPool } from "../../hooks/useOnchain";
import { getWeightedAPR } from "../../utils";
import { PoolTokenType } from "impermax-sdk";


export default function BorrowPosition() {
  const bData = [PoolTokenType.BorrowableA, PoolTokenType.BorrowableB].map(
    borrowable => useGenericCall(useOffchainAccountBorrowable(borrowable), async offchainAccountBorrowable => {
      const offchainBorrowable = offchainAccountBorrowable.getPoolToken();
      return {
        symbol: await offchainAccountBorrowable.getPoolToken().getSymbol(),
        borrowedValue: await offchainAccountBorrowable.getBorrowedValue(),
        borrowAPR: await offchainBorrowable.getBorrowAPR(),
        farmingAPR: await offchainBorrowable.getFarmingAPR(),
      }
    })
  );
  
  const data = useGenericCall(useOffchainAccountLendingPool(), async offchainAccountLendingPool => {
    const collateralValue = await offchainAccountLendingPool.getCollateral().getValue();
    const equityValue = await offchainAccountLendingPool.getBorrowerEquityValue();
    const totalAPR = await offchainAccountLendingPool.getLendingPool().getTotalAPR();
    const currentLeverage = collateralValue / equityValue;
    return {
      collateralValue,
      equityValue,
      currentLeverage,
      lpAPR: totalAPR * currentLeverage,
    }
  });
  const TWAPPrice = useGenericCall(useOnchainLendingPool(), async onchainLendingPool => onchainLendingPool.getTWAPPrice());
  const lendingPoolUrl = useLendingPoolUrl();

  if (!data || !bData[0] || !bData[1]) return (null);

  // Hide if equity is less than $1
  if (data.collateralValue < 1) return (null);

  const borrowAPR = getWeightedAPR(data.currentLeverage, bData[0].borrowedValue, bData[0].borrowAPR, bData[1].borrowedValue, bData[1].borrowAPR);
  const farmingAPR = getWeightedAPR(data.currentLeverage, bData[0].borrowedValue, bData[0].farmingAPR, bData[1].borrowedValue, bData[1].farmingAPR);
  const leveragedAPR = data.lpAPR + farmingAPR - borrowAPR;

  // TODO improve LiquidationPrices efficiency, overrideValues is not working anymore

  return (<Link to={lendingPoolUrl} className="position row">
    <Col className="currency-name">
      <PairLeftTitle/>
    </Col>
    <Col className="borrow-balance-details d-none d-sm-block">
      <Row>
        <Col className="col-12 col-lg-6">
          <Row>
            <Col className="col-6">Current Leverage</Col>
            <Col className="col-6 text-right">{formatLeverage(data.currentLeverage)}</Col>
          </Row>
          <Row>
            <Col className="col-6">Liquidation Range</Col>
            <Col className="col-6 text-right font-weight-bold">
              <LiquidationPrices />
            </Col>
          </Row>
          <Row>
            <Col className="col-6">TWAP Price</Col>
            <Col className="col-6 text-right">{TWAPPrice > 0 ? formatFloat(TWAPPrice, 4) : "-"}</Col>
          </Row>
          <Row>
            <Col className="col-6">Estimated APR</Col>
            <Col className="col-6 text-right">{formatPercentage(leveragedAPR)}</Col>
          </Row>
        </Col>
        <Col className="col-12 col-lg-6">
          <Row>
            <Col className="col-6">LP Balance</Col>
            <Col className="col-6 text-right">+{formatUSD(data.collateralValue)}</Col>
          </Row>
          {bData.map((data, i) => (<Row key={i}>
            <Col className="col-6">{data.symbol} Borrowed</Col>
            <Col className="col-6 text-right">-{formatUSD(data.borrowedValue)}</Col>
          </Row>))}
          <Row className="font-weight-bold">
            <Col className="col-6">LP Equity</Col>
            <Col className="col-6 text-right">{formatUSD(data.equityValue)}</Col>
          </Row>
        </Col>
      </Row>
    </Col>
  </Link>);
}